/**
 * Creates a stable JSON string representation of a value.
 *
 * Unlike `JSON.stringify`, this function produces consistent output regardless of
 * object key order. This is achieved by sorting object keys recursively before
 * serialization.
 *
 * This is useful for comparing values by their content rather than identity,
 * similar to how TanStack Query hashes query keys.
 *
 * @see https://github.com/TanStack/query/blob/main/packages/query-core/src/utils.ts#L227-L238
 *
 * @example
 * ```ts
 * stableStringify({ b: 2, a: 1 }) === stableStringify({ a: 1, b: 2 }) // true
 * stableStringify([{ b: 2, a: 1 }]) === stableStringify([{ a: 1, b: 2 }]) // true
 * ```
 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_, val) =>
    isPlainObject(val)
      ? Object.keys(val)
          .sort()
          .reduce<Record<string, unknown>>((result, key) => ({ ...result, [key]: val[key] }), {})
      : val,
  );
}

/**
 * Checks if a value is a plain object (not null, not an array, and has Object prototype).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  // Check for plain object prototype
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}
